import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPathWithoutLanguage } from '../../utils/languageRouting';
import { useIsHongKongIp } from '../../hooks/useIsHongKongIp';
import { goldNews, type NewsItem } from '../../data/goldNews';
import {
  DEFAULT_OG_IMAGE,
  SEO_LANGUAGES,
  SITE_NAME,
  SITE_URL,
  getAlternateUrls,
  getCanonicalUrl,
  getSeoLanguage,
  getSeoPage,
  isSeoLanguageCode,
  type SeoLanguageCode,
  type SeoContentLanguageCode,
} from '../../seo/seoConfig';

const MANAGED_META_ATTRIBUTE = 'data-jlbhk-seo';

type MetaDefinition = {
  name?: string;
  property?: string;
  content: string;
};

function upsertMeta(definition: MetaDefinition) {
  const selector = definition.name
    ? `meta[name="${definition.name}"]`
    : `meta[property="${definition.property}"]`;

  let element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement('meta');
    if (definition.name) element.setAttribute('name', definition.name);
    if (definition.property) element.setAttribute('property', definition.property);
    document.head.appendChild(element);
  }

  element.setAttribute('content', definition.content);
  element.setAttribute(MANAGED_META_ATTRIBUTE, 'true');
}

function upsertLink(rel: string, href: string, attributes: Record<string, string> = {}) {
  const hreflangSelector = attributes.hreflang ? `[hreflang="${attributes.hreflang}"]` : '';
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]${hreflangSelector}`);

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
  Object.entries(attributes).forEach(([key, value]) => element?.setAttribute(key, value));
  element.setAttribute(MANAGED_META_ATTRIBUTE, 'true');
}

function removeManagedAlternateLinks() {
  document.head
    .querySelectorAll<HTMLLinkElement>(`link[rel="alternate"][${MANAGED_META_ATTRIBUTE}="true"]`)
    .forEach((element) => element.remove());
}

function upsertStructuredData(id: string, payload: Record<string, unknown>) {
  let element = document.getElementById(id) as HTMLScriptElement | null;

  if (!element) {
    element = document.createElement('script');
    element.id = id;
    element.type = 'application/ld+json';
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(payload);
}

function removeStructuredData(id: string) {
  document.getElementById(id)?.remove();
}

function getNewsContentLanguage(contentCode: SeoContentLanguageCode): keyof NewsItem['lang'] {
  if (contentCode === 'hk') return 'zh-HK';
  if (contentCode === 'cn') return 'zh-CN';
  return 'en';
}

function getStableLanguageCode(currentLanguage: string): SeoLanguageCode {
  const normalized = currentLanguage.split('-')[0];

  if (isSeoLanguageCode(currentLanguage)) return currentLanguage;
  if (isSeoLanguageCode(normalized)) return normalized;

  return 'hk';
}

export default function SeoManager() {
  const location = useLocation();
  const { i18n } = useTranslation();
  const { canShowRestrictedContent } = useIsHongKongIp();

  useEffect(() => {
    const languageCode = getStableLanguageCode(i18n.language);
    const language = getSeoLanguage(languageCode);
    const requestedPagePath = getPathWithoutLanguage(window.location.pathname, __BASE_PATH__);
    const newsPathMatch = requestedPagePath.match(/^\/news\/([^/]+)\/?$/);
    const requestedNewsSlug = newsPathMatch ? decodeURIComponent(newsPathMatch[1]) : '';
    const newsItem = newsPathMatch
      ? goldNews.find((item) => item.slug === requestedNewsSlug)
        || (window.__JLBHK_PRERENDERED_NEWS__?.slug === requestedNewsSlug
          ? window.__JLBHK_PRERENDERED_NEWS__
          : undefined)
      : undefined;
    const restrictedRoute = ['/platform', '/market', '/android-download', '/android-download-choice', '/windows-download']
      .some((path) => requestedPagePath === path || requestedPagePath === `${path}/`);
    const pagePath = !canShowRestrictedContent && restrictedRoute ? '/' : requestedPagePath;
    const page = getSeoPage(pagePath);

    const safeSeo = {
      vn: {
        title: 'Gold Label Global Limited | Thông tin công ty và giấy phép',
        description: 'Tìm hiểu về công ty, giấy phép, thông báo chính thức và thông tin liên hệ của Gold Label Global Limited.',
        keywords: 'Gold Label Global Limited, JLBHK, thông tin công ty, giấy phép, thông báo chính thức',
      },
      hk: {
        title: '金利寶環球｜公司、牌照及官方資訊',
        description: '瀏覽金利寶環球 Gold Label Global Limited 的公司背景、牌照資質、官方公告及聯絡資料。',
        keywords: '金利寶, 金利寶環球, 香港金利寶, Gold Label Global Limited, JLBHK, 公司資料, 牌照資質',
      },
      cn: {
        title: '金利宝环球｜公司、牌照及官方资讯',
        description: '浏览金利宝环球 Gold Label Global Limited 的公司背景、牌照资质、官方公告及联系资料。',
        keywords: '金利宝, 金利宝环球, 香港金利宝, Gold Label Global Limited, JLBHK, 公司资料, 牌照资质',
      },
      en: {
        title: 'Gold Label Global Limited | Company and Licence Information',
        description: 'View company background, licence information, official notices and contact details for Gold Label Global Limited.',
        keywords: 'Gold Label Global Limited, JLBHK, company information, licence information, official notices',
      },
    }[language.contentCode];

    const newsBlock = newsItem?.lang[getNewsContentLanguage(language.contentCode)];
    const title = newsBlock
      ? `${newsBlock.title} | Gold Label Global`
      : canShowRestrictedContent
        ? page.title[language.contentCode]
        : safeSeo.title;
    const description = newsBlock
      ? newsBlock.summary
      : canShowRestrictedContent
        ? page.description[language.contentCode]
        : safeSeo.description;
    const keywords = newsItem
      ? [...newsItem.tags, 'Gold Label Global', 'JLBHK'].join(', ')
      : canShowRestrictedContent
        ? page.keywords[language.contentCode]
        : safeSeo.keywords;
    const seoPath = newsItem ? `/news/${newsItem.slug}` : page.path;
    const canonicalUrl = getCanonicalUrl(languageCode, seoPath);
    const alternateUrls = getAlternateUrls(seoPath);
    const socialImage = newsItem?.image
      ? newsItem.image.startsWith('http')
        ? newsItem.image
        : `${SITE_URL}${newsItem.image}`
      : DEFAULT_OG_IMAGE;

    document.documentElement.lang = language.htmlLang;
    document.documentElement.dir = languageCode === 'ug' ? 'rtl' : 'ltr';
    document.title = title;

    upsertMeta({ name: 'description', content: description });
    upsertMeta({ name: 'keywords', content: keywords });
    upsertMeta({ name: 'robots', content: 'index, follow, max-image-preview:large' });
    upsertMeta({ name: 'googlebot', content: 'index, follow, max-image-preview:large' });
    upsertMeta({ property: 'og:type', content: newsItem ? 'article' : 'website' });
    upsertMeta({ property: 'og:site_name', content: SITE_NAME });
    upsertMeta({ property: 'og:title', content: title });
    upsertMeta({ property: 'og:description', content: description });
    upsertMeta({ property: 'og:url', content: canonicalUrl });
    upsertMeta({ property: 'og:image', content: socialImage });
    upsertMeta({ property: 'og:locale', content: language.ogLocale });
    upsertMeta({ name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta({ name: 'twitter:title', content: title });
    upsertMeta({ name: 'twitter:description', content: description });
    upsertMeta({ name: 'twitter:image', content: socialImage });

    if (newsItem) {
      upsertMeta({ property: 'article:published_time', content: newsItem.publishedAt });
      upsertMeta({ property: 'article:section', content: newsItem.category });
    } else {
      document.head.querySelector('meta[property="article:published_time"]')?.remove();
      document.head.querySelector('meta[property="article:section"]')?.remove();
    }

    upsertLink('canonical', canonicalUrl);
    removeManagedAlternateLinks();
    alternateUrls.forEach((alternate) => {
      upsertLink('alternate', alternate.href, { hreflang: alternate.hreflang });
    });
    upsertLink('alternate', newsItem ? `${SITE_URL}/news/${newsItem.slug}` : SITE_URL + '/', {
      hreflang: 'x-default',
    });

    SEO_LANGUAGES.forEach((alternateLanguage) => {
      if (alternateLanguage.ogLocale !== language.ogLocale) {
        upsertMeta({ property: 'og:locale:alternate', content: alternateLanguage.ogLocale });
      }
    });

    upsertStructuredData('jlbhk-organization-jsonld', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Gold Label Global Limited',
      alternateName: ['金利寶環球', '金利宝环球', '金利寶', '金利宝', '香港金利寶', '香港金利宝', 'JLBHK'],
      url: SITE_URL + '/',
      logo: `${SITE_URL}/img/0c35285da7db83dd4744dcd7f9c41cc3.png`,
      description,
    });

    if (newsItem && newsBlock) {
      removeStructuredData('jlbhk-webpage-jsonld');
      upsertStructuredData('jlbhk-newsarticle-jsonld', {
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        headline: newsBlock.title,
        description: newsBlock.summary,
        image: [socialImage],
        datePublished: newsItem.publishedAt,
        dateModified: newsItem.publishedAt,
        inLanguage: language.code === 'vn' ? 'en' : language.htmlLang,
        mainEntityOfPage: canonicalUrl,
        author: {
          '@type': 'Organization',
          name: 'Gold Label Global Limited',
          url: SITE_URL + '/',
        },
        publisher: {
          '@type': 'Organization',
          name: 'Gold Label Global Limited',
          url: SITE_URL + '/',
          logo: {
            '@type': 'ImageObject',
            url: `${SITE_URL}/img/0c35285da7db83dd4744dcd7f9c41cc3.png`,
          },
        },
      });
    } else {
      removeStructuredData('jlbhk-newsarticle-jsonld');
      upsertStructuredData('jlbhk-webpage-jsonld', {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: canonicalUrl,
        inLanguage: language.htmlLang,
        isPartOf: {
          '@type': 'WebSite',
          name: SITE_NAME,
          url: SITE_URL + '/',
        },
      });
    }
  }, [canShowRestrictedContent, i18n.language, location.pathname]);

  return null;
}
